import React, { useMemo } from "react";
import {
	Box,
	Typography,
	Button,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TableContainer,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import {
	StatusBadge,
	capitalizeFirstLetter,
	formatDate,
	getCodeDescription,
} from "../../utils/functions";
import { useSelector } from "react-redux";

const EquipmentTab = ({
	vehicle,
	equipment,
	employees,
	onViewEquipment,
	onAssignEquipment,
	canAssign = true,
	classes,
}) => {
	const theme = useTheme();
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	// Filter equipment assigned to this vehicle
	const vehEquipment = useMemo(() => {
		return (equipment || []).sort((a, b) =>
			(b.assignedDate || "").localeCompare(a.assignedDate || ""),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [equipment, vehicle?.id]);

	// Check if inspection is overdue (>365 days)
	const isInspectionOverdue = (lastInspectionDate) => {
		if (!lastInspectionDate) return true;
		const daysSince = Math.floor(
			(new Date() - new Date(lastInspectionDate)) / (1000 * 60 * 60 * 24),
		);
		return daysSince > 365;
	};

	// Get employee display name
	const getEmployeeDisplay = (employeeId) => {
		if (!employeeId) return "—";
		const emp = employees?.find(
			(e) =>
				e.value === employeeId ||
				e.value === String(employeeId) ||
				e.id === employeeId,
		);
		return emp ? emp.label : "—";
	};

	return (
		<Box>
			{/* Header with count and assign button */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					{vehEquipment.length} equipment item
					{vehEquipment.length !== 1 ? "s" : ""} assigned to this
					vehicle
				</Typography>
				{canAssign && (
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={onAssignEquipment}
					>
						+ Assign Equipment
					</Button>
				)}
			</Box>

			{/* Equipment Table or Empty State */}
			{vehEquipment.length === 0 ? (
				<Box
					sx={{
						textAlign: "center",
						py: 6,
						color: "text.secondary",
					}}
				>
					<Box sx={{ fontSize: 48, mb: 1 }}>🔫</Box>
					<Typography>
						No equipment assigned to this vehicle
					</Typography>
					<Typography
						variant="caption"
						color="text.disabled"
						sx={{ display: "block", mt: 1 }}
					>
						Click "Assign Equipment" to add items from the equipment
						inventory
					</Typography>
				</Box>
			) : (
				<TableContainer
					sx={{
						border: 1,
						borderColor: "divider",
						borderRadius: 1,
						overflow: "hidden",
					}}
				>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ bgcolor: "action.hover" }}>
								{[
									"Type",
									"Serial #",
									"Property #",
									"Asset Tag",
									"Brand / Model",
									"Condition",
									"Status",
									"Assigned Employee",
									"Last Audit",
								].map((header) => (
									<TableCell
										key={header}
										sx={{
											fontWeight: 700,
											fontSize: 10,
											textTransform: "uppercase",
											letterSpacing: "0.05em",
										}}
									>
										{header}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{vehEquipment.map((eq) => {
								const overdue = isInspectionOverdue(
									eq.lastInspectionDate,
								);
								return (
									<TableRow
										key={eq.id}
										hover
										onClick={() => onViewEquipment(eq)}
										sx={{
											cursor: "pointer",
											"&:hover": {
												bgcolor: "action.hover",
											},
										}}
									>
										<TableCell sx={{ fontWeight: 600 }}>
											{getCodeDescription(
												eq.type,
												dictionary.codePerEquipmentTypes,
											) ||
												eq.typeInfo?.Description ||
												"—"}
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													color: theme.palette.info
														.main,
												}}
											>
												{eq.serialNumber || "—"}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{eq.propertyNumber || "—"}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{eq.assetTag || "—"}
											</Typography>
										</TableCell>
										<TableCell>
											{`${eq.brand || ""} ${eq.model || ""}`.trim() ||
												"—"}
										</TableCell>
										<TableCell>
											<StatusBadge
												status={capitalizeFirstLetter(
													getCodeDescription(
														eq.condition,
														dictionary.codePerEquipmentConditions,
													) ||
														eq.conditionInfo
															?.Description,
												)}
											/>
										</TableCell>
										<TableCell>
											<StatusBadge
												status={capitalizeFirstLetter(
													getCodeDescription(
														eq.status,
														dictionary.codePerEquipmentStatus,
													) ||
														eq.statusInfo
															?.Description,
												)}
											/>
										</TableCell>
										<TableCell
											sx={{
												color: "text.secondary",
												fontSize: 12,
											}}
										>
											{getEmployeeDisplay(
												eq.perPersonnelId ||
													eq.assignedOfficerID,
											)}
										</TableCell>
										<TableCell>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
												}}
											>
												<Typography
													variant="caption"
													sx={{
														fontFamily: "monospace",
													}}
												>
													{eq.lastInspectionDate
														? formatDate(
																eq.lastInspectionDate,
															)
														: "Never"}
												</Typography>
												{overdue && (
													<Chip
														label="OVERDUE"
														size="small"
														color="error"
														sx={{
															height: 18,
															fontSize: 9,
															fontWeight: 700,
														}}
													/>
												)}
											</Box>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	);
};

export default EquipmentTab;
